import React, { Component } from 'react';
import axios from 'axios';
import {
  Box, Button, Checkbox, FormControlLabel, TextField,
  Typography, Stack, Card as MuiCard, Divider, Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { withRouter } from './withRouter';

const GlassCard = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: { maxWidth: 460 },
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(11px)',
  border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 20,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15), 0 6px 20px 0 rgba(0,0,0,0.08)',
  ...theme.applyStyles('dark', {
    background: 'rgba(30,30,40,0.35)',
    border: '1px solid rgba(255,255,255,0.12)',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  marginTop: '10vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage: 'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.withCredentials = true;

class SignIn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      demoName: '',
      demoCode: '',
      loginError: '',
      demoError: '',
      demoSuccess: '',
      verifyMessage: '',
      isDemoSignup: false,
      loadingVerify: false,
      submitting: false,
    };
  }

  componentDidMount() {
    axios.get('/api/user-info/')
      .then(() => this.props.navigate('/explore'))
      .catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const urlDemoCode = params.get('code') || params.get('demo') || '';
    if (urlDemoCode) this.setState({ demoCode: urlDemoCode, isDemoSignup: true });
    if (params.get('demoVerify') !== '1') return;
    const uid = params.get('uid');
    const token = params.get('token');
    if (!uid || !token) return;

    this.setState({ loadingVerify: true });
    axios.post('/api/user/demo-verify/', { uid, token })
      .then(() => {
        this.setState({ verifyMessage: 'Email verified. You are now signed in.', loadingVerify: false });
        this.props.navigate('/explore');
      })
      .catch((error) => {
        this.setState({
          verifyMessage: error?.response?.data?.error || 'Verification link is invalid or expired.',
          loadingVerify: false,
        });
      });
  }

  submitLogin = async (e) => {
    e.preventDefault();
    const { email, password } = this.state;
    this.setState({ submitting: true, loginError: '' });
    try {
      await axios.post('/api/user/login/', { email, password });
      this.props.navigate('/explore');
    } catch (error) {
      this.setState({ loginError: error?.response?.data?.error || 'Invalid email or password.' });
    } finally {
      this.setState({ submitting: false });
    }
  };

  submitDemoSignup = async (e) => {
    e.preventDefault();
    const { email, password, demoName, demoCode } = this.state;
    this.setState({ submitting: true, demoError: '', demoSuccess: '' });
    try {
      const response = await axios.post('/api/user/demo-signup/', {
        email,
        password,
        name: demoName,
        demo_code: demoCode,
      });
      const verifyLink = response?.data?.verify_url;
      this.setState({
        demoSuccess: verifyLink
          ? `Demo account created. Dev verify link: ${verifyLink}`
          : 'Demo signup created. Check your email to verify and activate access.',
      });
    } catch (error) {
      this.setState({ demoError: error?.response?.data?.error || 'Unable to create demo signup.' });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const {
      email, password, demoName, demoCode,
      loginError, demoError, demoSuccess, verifyMessage,
      isDemoSignup, loadingVerify, submitting,
    } = this.state;

    return (
      <SignInContainer
        direction="column"
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, hsl(205,70%,92%) 0%, hsl(150,50%,93%) 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 0,
        }}
      >
        <GlassCard sx={{ p: { xs: 3, sm: 5 }, width: { xs: '91%', sm: 420, md: 480 }, gap: 1.5 }}>
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography variant="h5" fontWeight={800} color="primary">
              Electorate Profiles
            </Typography>
            <Typography variant="body2" color="text.secondary">New Zealand</Typography>
          </Box>

          <Typography variant="h4" fontWeight={700} align="center" sx={{ letterSpacing: 0.5 }}>
            {isDemoSignup ? 'Sign up for a demo' : 'Member Sign-In'}
          </Typography>

          {verifyMessage && (
            <Alert severity={loadingVerify ? 'info' : 'success'}>{verifyMessage}</Alert>
          )}

          <Box
            component="form"
            onSubmit={isDemoSignup ? this.submitDemoSignup : this.submitLogin}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            {isDemoSignup && (
              <TextField
                label="Full name (optional)"
                fullWidth
                value={demoName}
                onChange={(e) => this.setState({ demoName: e.target.value, demoError: '' })}
              />
            )}
            {isDemoSignup && (
              <TextField
                label="Demo code (optional)"
                fullWidth
                value={demoCode}
                onChange={(e) => this.setState({ demoCode: e.target.value, demoError: '' })}
              />
            )}

            <TextField
              type="email"
              label="Email Address"
              fullWidth
              value={email}
              onChange={(e) => this.setState({ email: e.target.value, loginError: '', demoError: '', demoSuccess: '' })}
            />

            <TextField
              type="password"
              label="Password"
              fullWidth
              value={password}
              onChange={(e) => this.setState({ password: e.target.value, loginError: '', demoError: '' })}
            />

            {!isDemoSignup && (
              <FormControlLabel control={<Checkbox color="primary" />} label="Remember me" />
            )}

            {loginError && !isDemoSignup && <Alert severity="error">{loginError}</Alert>}
            {demoError && isDemoSignup && <Alert severity="error">{demoError}</Alert>}
            {demoSuccess && isDemoSignup && <Alert severity="success">{demoSuccess}</Alert>}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting || loadingVerify}
              sx={{ py: 1.3, fontWeight: 600, letterSpacing: 0.5 }}
            >
              {isDemoSignup ? 'Create Demo Account' : 'Sign In'}
            </Button>
          </Box>

          <Divider sx={{ my: 0.5 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="center" spacing={1.5} mb={0.5}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              {isDemoSignup ? 'Already have access?' : 'Need trial access?'}
            </Typography>
            <Button
              size="medium"
              onClick={() => this.setState({ isDemoSignup: !isDemoSignup, loginError: '', demoError: '', demoSuccess: '' })}
              sx={{ textTransform: 'none', fontWeight: 700, letterSpacing: 0.5 }}
              variant="outlined"
            >
              {isDemoSignup ? 'Back to sign in' : 'Sign up for a demo'}
            </Button>
          </Stack>
        </GlassCard>
      </SignInContainer>
    );
  }
}

export default withRouter(SignIn);
